import React, { useState, useRef } from "react";
import ReactModal from "react-modal";
import close from "../../assets/icons/Cross.svg";
import {
  FaMicrophone,
  FaStop,
  FaPlay,
  FaTrash,
  FaCheck,
  FaPause,
} from "react-icons/fa";
import { IoMdClose } from "react-icons/io";

const AudioRecorderModal = ({ isOpen, onClose, onSave }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isRecorded, setIsRecorded] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [playbackTime, setPlaybackTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunks = useRef([]);
  const audioRef = useRef(null);
  const timerRef = useRef(null);

  const startRecording = async () => {
    setAudioUrl(null);
    setAudioFile(null);
    setIsRecorded(false);
    setRecordingTime(0);
    setPlaybackTime(0);
    setAudioDuration(0);
    setIsPaused(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new window.MediaRecorder(stream);

      audioChunks.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunks.current, { type: "audio/webm" });
        const file = new File([audioBlob], "audio-capture.webm", {
          type: "audio/webm",
        });
        setAudioFile(file);
        setAudioUrl(URL.createObjectURL(audioBlob));
        setIsRecorded(true);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);

      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording && !isPaused) {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      clearInterval(timerRef.current);
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && isRecording && isPaused) {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      clearInterval(timerRef.current);
    }
  };

  const resetRecording = () => {
    setIsRecording(false);
    setIsPaused(false);
    setIsRecorded(false);
    setAudioUrl(null);
    setAudioFile(null);
    setRecordingTime(0);
    setPlaybackTime(0);
    setAudioDuration(0);
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setPlaybackTime(0);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
  };

  const handleSave = () => {
    if (audioFile) {
      onSave(audioFile, recordingTime);
      onClose();
    }
  };

  const handleClose = () => {
    setIsRecording(false);
    setIsPaused(false);
    setIsRecorded(false);
    setAudioUrl(null);
    setAudioFile(null);
    setRecordingTime(0);
    setPlaybackTime(0);
    setAudioDuration(0);
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    onClose(false);
  };

  // Format time as mm:ss
  const formatTime = (sec) => {
    const m = Math.floor(sec / 60)
      .toString()
      .padStart(2, "0");
    const s = (sec % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  // Progress bar for playback
  const handleAudioTimeUpdate = () => {
    if (audioRef.current) {
      setPlaybackTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      const duration = audioRef.current.duration;
      setAudioDuration(duration && isFinite(duration) ? duration : 0);
    }
  };

  const handleSeek = (e) => {
    if (audioRef.current) {
      audioRef.current.currentTime = e.target.value;
      setPlaybackTime(e.target.value);
    }
  };

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel="Audio Recorder"
      className="custom-modal absolute z-[40] flex h-screen w-screen items-center justify-center bg-black bg-opacity-50 py-6"
      ariaHideApp={false}
    >
      <div className="MODAL-BODY relative flex h-fit w-[90vw] max-w-[400px] flex-col items-center justify-center rounded-2xl bg-blackishV2 p-8 text-white md:absolute">
        <button
          className="absolute -right-2 -top-2 z-50 flex aspect-square items-center justify-center rounded-full bg-yellowish p-0.5 text-xl text-white"
          onClick={handleClose}
        >
          <IoMdClose />
        </button>

        <h2 className="mb-6 text-2xl font-semibold">Audio Recorder</h2>

        {/* Recording UI */}
        {!isRecording && !isRecorded && (
          <button
            className="flex flex-col items-center justify-center rounded-full bg-yellowish p-6 text-blackish shadow-lg transition hover:scale-105"
            onClick={startRecording}
            aria-label="Start Recording"
          >
            <FaMicrophone size={36} />
            <span className="mt-2 text-xs font-semibold">Tap to record</span>
          </button>
        )}

        {isRecording && (
          <div className="flex flex-col items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="h-3 w-3 animate-pulse rounded-full bg-red-500"></span>
              <span className="font-mono text-lg">
                {formatTime(recordingTime)}
              </span>
            </div>
            <div className="flex gap-4">
              {!isPaused ? (
                <button
                  className="flex items-center justify-center rounded-full bg-yellowish p-5 text-blackish shadow-lg transition hover:scale-105"
                  onClick={pauseRecording}
                  aria-label="Pause Recording"
                >
                  <FaPause size={28} />
                </button>
              ) : (
                <button
                  className="flex items-center justify-center rounded-full bg-yellowish p-5 text-blackish shadow-lg transition hover:scale-105"
                  onClick={resumeRecording}
                  aria-label="Resume Recording"
                >
                  <FaMicrophone size={28} />
                </button>
              )}
              <button
                className="flex items-center justify-center rounded-full bg-red-500 p-5 text-white shadow-lg transition hover:scale-105"
                onClick={stopRecording}
                aria-label="Stop Recording"
              >
                <FaStop size={28} />
              </button>
            </div>
            <span className="mt-2 text-xs font-semibold">
              {isPaused ? "Paused" : "Recording..."}
            </span>
          </div>
        )}

        {/* Playback UI */}
        {isRecorded && !isRecording && (
          <div className="flex w-full flex-col items-center gap-4">
            <div className="flex w-full items-center gap-2">
              <button
                className="flex items-center justify-center rounded-full bg-red-500 p-3 text-white shadow-lg transition hover:scale-105"
                onClick={resetRecording}
                aria-label="Delete"
              >
                <FaTrash size={18} />
              </button>
              <audio
                ref={audioRef}
                src={audioUrl}
                controls
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={handleAudioEnded}
                onTimeUpdate={handleAudioTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                className="w-full"
                style={{ outline: "none" }}
              />
              <button
                className="flex items-center justify-center rounded-full bg-green-500 p-3 text-white shadow-lg transition hover:scale-105"
                onClick={handleSave}
                aria-label="Save"
              >
                <FaCheck size={18} />
              </button>
            </div>
            <span className="mt-2 text-xs font-semibold">Preview & Save</span>
          </div>
        )}
      </div>
    </ReactModal>
  );
};

export default AudioRecorderModal;
